import { getHistoryStorageCopy } from '../i18n';
import { Sheet, SheetHistoryEntry, HistoryActionType, Pouch, Group, SheetSnapshot, SheetLayer } from '../types';
import { calculateGroupPlaceholders } from '../logic/GroupLogic';
import {
  getSortedLayers,
  layersForHistoryEntryContext,
  resolveActiveLayerId,
} from '../logic/LayerLogic';

function activeLayerIdForHistory(sheet: Sheet): string | undefined {
  if (!sheet.layers?.length) return undefined;
  return resolveActiveLayerId(sheet);
}

function activeLayerIdFromEntry(sheet: Sheet, entry: SheetHistoryEntry): string | undefined {
  const layers = entry.layers ?? sheet.layers;
  if (!layers?.length) return undefined;
  if (entry.activeLayerId && layers.some(l => l.id === entry.activeLayerId)) {
    return entry.activeLayerId;
  }
  return resolveActiveLayerId({ ...sheet, layers });
}

/** Undo: 取り消す操作（current）が行われたレイヤーを表示。レイヤー切替は履歴に載らないため除外済み */
function activeLayerIdForUndo(sheet: Sheet, currentState: SheetHistoryEntry): string | undefined {
  return activeLayerIdFromEntry(sheet, currentState);
}

/** Redo: やり直す操作（target）が行われたレイヤーを表示 */
function activeLayerIdForRedo(sheet: Sheet, targetState: SheetHistoryEntry): string | undefined {
  return activeLayerIdFromEntry(sheet, targetState);
}

/** 履歴マージ: 同一アクティブレイヤー上の連続操作だけ統合する */
function sameHistoryActiveLayer(
  sheet: Sheet,
  previous?: string,
  current?: string
): boolean {
  if (!sheet.layers?.length) return true;
  const prev = previous ?? resolveActiveLayerId(sheet);
  const cur = current ?? resolveActiveLayerId(sheet);
  return prev === cur;
}

function layersForHistory(sheet: Sheet): SheetLayer[] | undefined {
  if (!sheet.layers?.length) return undefined;
  return sheet.layers.map(l => ({ ...l }));
}

function balancingTargetsForHistory(sheet: Sheet): Record<string, number> | undefined {
  return sheet.balancingTargets ? { ...sheet.balancingTargets } : undefined;
}

/** 履歴エントリから layers を復元 */
function resolveHistoryLayers(entry: SheetHistoryEntry, sheet: Sheet): SheetLayer[] | undefined {
  return layersForHistoryEntryContext(entry, getSortedLayers(sheet));
}

function layerFieldsFromEntry(sheet: Sheet, entry: SheetHistoryEntry): Pick<Sheet, 'layers' | 'balancingTargets'> {
  const layers = resolveHistoryLayers(entry, sheet);
  if (!layers?.length) {
    return {};
  }
  let balancingTargets = entry.balancingTargets;
  if (balancingTargets) {
    const allowed = new Set(layers.map(l => l.id));
    balancingTargets = Object.fromEntries(
      Object.entries(balancingTargets).filter(([id]) => allowed.has(id))
    );
    if (Object.keys(balancingTargets).length === 0) {
      balancingTargets = undefined;
    }
  }
  return { layers, balancingTargets };
}

export class HistoryLogic {
  /** EDIT_POUCH_VALUE の targetId 判定・マージ対象（roundUnit のみ変更も含む） */
  static pouchEditDiffersForHistory(original: Pouch, updated: Pouch): boolean {
    if (
      original.value !== updated.value ||
      original.multiplier !== updated.multiplier ||
      original.roundUnit !== updated.roundUnit ||
      original.oppositeToTotal !== updated.oppositeToTotal ||
      original.isTime !== updated.isTime
    ) {
      return true;
    }
    const oLv = original.layerValues;
    const uLv = updated.layerValues;
    if (!oLv && !uLv) return false;
    if (!oLv || !uLv) return true;
    const layerIds = Array.from(new Set([...Object.keys(oLv), ...Object.keys(uLv)]));
    for (const lid of layerIds) {
      const oe = oLv[lid];
      const ue = uLv[lid];
      if (!oe && !ue) continue;
      if (!oe || !ue) return true;
      if (oe.roundUnit !== ue.roundUnit) return true;
      if (oe.value !== ue.value) return true;
      if (oe.multiplier !== ue.multiplier) return true;
    }
    return false;
  }

  /** 履歴用に1パウチだけ UI フラグを整理（全件 map より軽い） */
  private static cleanPouchForHistory(pouch: Pouch): Pouch {
    const cleanPouch = { ...pouch };
    const pouchAny = cleanPouch as any;
    delete pouchAny._animateFromZero;
    delete pouchAny._isDragging;
    delete pouchAny._isMoving;
    delete pouchAny._isDeleting;
    pouchAny._animateMultiplier = true;
    pouchAny._animateSignChange = true;
    return cleanPouch;
  }

  private static balancingMetaForHistoryEntry(sheet: Sheet): Pick<
    SheetHistoryEntry,
    | 'balancingPouchId'
    | 'balancingTargetTotal'
    | 'balancingPreferredSign'
    | 'activeLayerId'
    | 'layers'
    | 'balancingTargets'
  > {
    return {
      balancingPouchId: sheet.balancingPouchId ?? null,
      balancingTargetTotal: sheet.balancingTargetTotal,
      balancingPreferredSign: sheet.balancingPreferredSign,
      activeLayerId: activeLayerIdForHistory(sheet),
      layers: layersForHistory(sheet),
      balancingTargets: balancingTargetsForHistory(sheet),
    };
  }

  /** 値変更の連続マージ: 変更パウチ1件だけコピーし groups / placeholder 再計算を省略 */
  private static mergeSinglePouchHistoryEntry(
    sheet: Sheet,
    latestEntry: SheetHistoryEntry,
    pouches: Pouch[],
    targetId: string
  ): Sheet {
    const source = pouches.find(p => p.id === targetId);
    if (!source || !sheet.history?.length || sheet.historyIndex < 0) {
      return sheet;
    }
    const cleanPouches = latestEntry.pouches.map(p =>
      p.id === targetId ? HistoryLogic.cleanPouchForHistory(source) : p
    );
    const updatedHistory = [...sheet.history];
    updatedHistory[sheet.historyIndex] = {
      ...latestEntry,
      ...HistoryLogic.balancingMetaForHistoryEntry(sheet),
      timestamp: new Date(),
      pouches: cleanPouches,
    };
    return { ...sheet, history: updatedHistory };
  }

  static addEntry(
    sheet: Sheet, 
    groups: Group[], 
    pouches: Pouch[], 
    action: HistoryActionType, 
    description: string,
    targetId?: string,
    forceNewEntry?: boolean
  ): Sheet {
    const latestEntryForLightMerge =
      sheet.history?.length && sheet.historyIndex >= 0
        ? sheet.history[sheet.historyIndex]
        : undefined;

    if (
      !forceNewEntry &&
      targetId !== undefined &&
      sheet.historyIndex > 0 &&
      latestEntryForLightMerge &&
      action === HistoryActionType.EDIT_POUCH_VALUE &&
      latestEntryForLightMerge.type === HistoryActionType.EDIT_POUCH_VALUE &&
      latestEntryForLightMerge.targetId === targetId &&
      sameHistoryActiveLayer(
        sheet,
        latestEntryForLightMerge.activeLayerId,
        activeLayerIdForHistory(sheet)
      )
    ) {
      return HistoryLogic.mergeSinglePouchHistoryEntry(
        sheet,
        latestEntryForLightMerge,
        pouches,
        targetId
      );
    }

    if (
      !forceNewEntry &&
      targetId !== undefined &&
      sheet.historyIndex > 0 &&
      latestEntryForLightMerge &&
      action === HistoryActionType.CHANGE_POUCH_COLOR &&
      latestEntryForLightMerge.type === HistoryActionType.CHANGE_POUCH_COLOR &&
      latestEntryForLightMerge.targetId === targetId &&
      sameHistoryActiveLayer(
        sheet,
        latestEntryForLightMerge.activeLayerId,
        activeLayerIdForHistory(sheet)
      )
    ) {
      return HistoryLogic.mergeSinglePouchHistoryEntry(
        sheet,
        latestEntryForLightMerge,
        pouches,
        targetId
      );
    }

    // 一時的なアニメーション用プロパティを除外したpouchesとgroupsをコピー
    const cleanPouches = pouches.map(pouch => HistoryLogic.cleanPouchForHistory(pouch));

    // グループにプレースホルダー名を計算して設定
    let cleanGroups = groups.map(group => {
      const cleanGroup = { ...group };
      const groupAny = cleanGroup as any;
      
      // 各種UIフラグを削除
      delete groupAny._isCloning;
      delete groupAny._animateSubtotal;
      
      return cleanGroup;
    });

    // グループをGROUP_COLORSの順、SUBTOTALの高い順にソートしてプレースホルダー名を計算
    cleanGroups = calculateGroupPlaceholders(cleanGroups, cleanPouches);

    const entry: SheetHistoryEntry = {
      timestamp: new Date(),
      groups: cleanGroups,
      pouches: cleanPouches,
      description,
      type: action,
      targetId,
      balancingPouchId: sheet.balancingPouchId ?? null,
      balancingTargetTotal: sheet.balancingTargetTotal,
      balancingPreferredSign: sheet.balancingPreferredSign,
      activeLayerId: activeLayerIdForHistory(sheet),
      layers: layersForHistory(sheet),
      balancingTargets: balancingTargetsForHistory(sheet),
      ...(action === HistoryActionType.ADD_POUCH ? { addPouchAwaitingFirstValue: true as const } : {}),
      ...(action === HistoryActionType.ADD_SHEET_LAYER ? { addLayerAwaitingFirstRename: true as const } : {}),
    };
  
    // 履歴がない場合は新しいエントリを追加
    if (!sheet.history || sheet.history.length === 0) {
      return {
        ...sheet,
        history: [entry],
        historyIndex: 0
      };
    }

    // 新規パウチ ADD の直後の初回値確定: EDIT を別エントリにせず、ADD を最終値のスナップショットで上書き
    const latestForAddMerge = sheet.history[sheet.historyIndex];
    if (
      action === HistoryActionType.EDIT_POUCH_VALUE &&
      targetId !== undefined &&
      sheet.historyIndex >= 0 &&
      latestForAddMerge.type === HistoryActionType.ADD_POUCH &&
      latestForAddMerge.targetId === targetId &&
      latestForAddMerge.addPouchAwaitingFirstValue === true
    ) {
      const updatedHistory = [...sheet.history];
      updatedHistory[sheet.historyIndex] = {
        ...latestForAddMerge,
        timestamp: new Date(),
        groups: cleanGroups,
        pouches: cleanPouches,
        layers: layersForHistory(sheet),
        balancingTargets: balancingTargetsForHistory(sheet),
        addPouchAwaitingFirstValue: false,
      };
      return {
        ...sheet,
        history: updatedHistory,
      };
    }

    // 新規レイヤー ADD の直後の初回リネーム: Edit Layer を別エントリにせず ADD を上書き
    const latestForLayerAddMerge = sheet.history[sheet.historyIndex];
    if (
      action === HistoryActionType.RENAME_SHEET_LAYER &&
      targetId !== undefined &&
      sheet.historyIndex >= 0 &&
      latestForLayerAddMerge.type === HistoryActionType.ADD_SHEET_LAYER &&
      latestForLayerAddMerge.targetId === targetId &&
      latestForLayerAddMerge.addLayerAwaitingFirstRename === true
    ) {
      const updatedHistory = [...sheet.history];
      updatedHistory[sheet.historyIndex] = {
        ...latestForLayerAddMerge,
        timestamp: new Date(),
        groups: cleanGroups,
        pouches: cleanPouches,
        layers: layersForHistory(sheet),
        balancingTargets: balancingTargetsForHistory(sheet),
        addLayerAwaitingFirstRename: false,
      };
      return {
        ...sheet,
        history: updatedHistory,
      };
    }

    // 以下のアクションタイプは連続した同一対象の操作を1つのエントリとして扱う
    const mergeableActions = [
      HistoryActionType.EDIT_POUCH_VALUE,
      HistoryActionType.CHANGE_GROUP_COLOR,
      HistoryActionType.CHANGE_POUCH_COLOR,
      HistoryActionType.PASTE_BULK_UPDATE,
      HistoryActionType.RENAME_SHEET_LAYER,
    ];
    
    // 最新のエントリ
    const latestEntry = sheet.history[sheet.historyIndex];
    
    // 連続した同一対象への操作かどうかを判定（ADD_POUCH / DELETE_POUCH の連続マージは行わない）
    const shouldMergeEntry = !forceNewEntry && (
      (mergeableActions.includes(action) && // 統合対象のアクションタイプか
      latestEntry.type === action && // 前回と同じアクションタイプか
      latestEntry.targetId === targetId && // 同じ対象への操作か
      targetId !== undefined && // 対象IDが指定されているか
      sameHistoryActiveLayer(sheet, latestEntry.activeLayerId, entry.activeLayerId) &&
      sheet.historyIndex > 0) || // historyIndexが0より大きい場合のみ統合する（[0]を保護）
      // タイトル編集は常に最新のエントリを上書き（タイトル編集をどのアクションの一部としても適用できるようにする）
      ((action === HistoryActionType.EDIT_POUCH_TITLE || action === HistoryActionType.EDIT_GROUP_TITLE) && 
       sheet.historyIndex > 0)  // historyIndexが0より大きい場合のみ統合する（[0]を保護）
    );
    
    
    if (shouldMergeEntry) {
      if (
        action === HistoryActionType.EDIT_POUCH_VALUE &&
        targetId !== undefined
      ) {
        return HistoryLogic.mergeSinglePouchHistoryEntry(
          sheet,
          latestEntry,
          pouches,
          targetId
        );
      }
      if (
        action === HistoryActionType.CHANGE_POUCH_COLOR &&
        targetId !== undefined
      ) {
        return HistoryLogic.mergeSinglePouchHistoryEntry(
          sheet,
          latestEntry,
          pouches,
          targetId
        );
      }

      // 新しいエントリで上書き（historyIndexは変更しない）
      const updatedHistory = [...sheet.history];
      
      // タイトル編集の場合は、アクションタイプとdescriptionは元のままにしてpouchesとgroupsだけを更新
      if (action === HistoryActionType.EDIT_POUCH_TITLE || action === HistoryActionType.EDIT_GROUP_TITLE) {
        updatedHistory[sheet.historyIndex] = {
          ...updatedHistory[sheet.historyIndex],
          pouches: cleanPouches,
          groups: cleanGroups,
          layers: layersForHistory(sheet),
          balancingTargets: balancingTargetsForHistory(sheet),
        };
      } else {
        // 通常の上書き
        updatedHistory[sheet.historyIndex] = entry;
      }
      
      return {
        ...sheet,
        history: updatedHistory,
      };
    }
    
    // 通常の履歴追加処理
    const startIndex = sheet.historyIndex + 1;
    const newHistory = sheet.history.slice(0, startIndex);
    const updatedHistory = [...newHistory, entry];


    const result = {
      ...sheet,
      history: updatedHistory,
      historyIndex: startIndex
    };
    
    return result;
  }

  /**
   * 直近の履歴が「指定パウチの Add Pouch」のときだけそのエントリを削除する。
   * キーボード新規を Esc で破棄したとき、Add の履歴も残さない用途。
   */
  static stripLatestHistoryEntryIfAddPouchForTarget(sheet: Sheet, pouchId: string): Sheet | null {
    if (!sheet.history?.length || sheet.historyIndex == null || sheet.historyIndex < 0) {
      return null;
    }
    const entry = sheet.history[sheet.historyIndex];
    if (entry.type !== HistoryActionType.ADD_POUCH || entry.targetId !== pouchId) {
      return null;
    }
    if (sheet.historyIndex === 0) {
      return null;
    }
    const newHistory = sheet.history.slice(0, sheet.historyIndex);
    const newIndex = sheet.historyIndex - 1;
    return {
      ...sheet,
      history: newHistory,
      historyIndex: newIndex
    };
  }

  static undo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex <= 0) return null;

    const newIndex = sheet.historyIndex - 1;
    const targetState = sheet.history[newIndex];
    const currentState = sheet.history[sheet.historyIndex];
    
    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );
    let targetPouches = animationResult.pouches;

    // 統一的なアニメーション判定の結果を使用
    let targetGroups = animationResult.groups;
    
    // ADD_GROUPからのundoの場合、addingフラグを設定しない（通常の状態に戻す）
    if (currentState.type !== HistoryActionType.DELETE_GROUP && currentState.type !== HistoryActionType.MERGE_GROUP && targetState.type === HistoryActionType.ADD_GROUP) {
      // currentStateのグループにaddingフラグがついている場合は削除
      targetGroups = targetGroups.map(g => {
        if ((g as any)._isAdding) {
          return { ...g, _isAdding: false } as Group;
        }
        return g;
      });
    }

    const newSheet = {
      ...sheet,
      historyIndex: newIndex,
      pouches: targetPouches,
      groups: targetGroups,
      balancingPouchId: targetState.balancingPouchId ?? null,
      balancingTargetTotal: targetState.balancingTargetTotal,
      balancingPreferredSign: targetState.balancingPreferredSign,
      activeLayerId: activeLayerIdForUndo(sheet, currentState),
      ...layerFieldsFromEntry(sheet, targetState),
    };

    return {
      newSheet,
      newPouches: targetPouches,
      newGroups: targetGroups
    };
  }

  static redo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex >= sheet.history.length - 1) return null;

    const newIndex = sheet.historyIndex + 1;
    const targetState = sheet.history[newIndex];
    const currentState = sheet.history[sheet.historyIndex];
    
    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );
    let targetPouches = animationResult.pouches;

    // 統一的なアニメーション判定の結果を使用
    let targetGroups = animationResult.groups;

    return {
      newSheet: {
        ...sheet,
        historyIndex: newIndex,
        pouches: targetPouches,
        groups: targetGroups,
        balancingPouchId: targetState.balancingPouchId ?? null,
        balancingTargetTotal: targetState.balancingTargetTotal,
        balancingPreferredSign: targetState.balancingPreferredSign,
        activeLayerId: activeLayerIdForRedo(sheet, targetState),
        ...layerFieldsFromEntry(sheet, targetState),
      },
      newPouches: targetPouches,
      newGroups: targetGroups
    };
  }

  static createSnapshot(
    sheet: Sheet,
    pouches: Pouch[],
    groups: Group[],
    name: string,
    description: string
  ): Sheet {
    // スナップショット作成時も同様に一時的なプロパティを除外
    const cleanPouches = pouches.map(pouch => {
      const cleanPouch = { ...pouch };
      const pouchAny = cleanPouch as any;
      
      delete pouchAny._animateFromZero;
      delete pouchAny._isDragging;
      delete pouchAny._isMoving;
      delete pouchAny._isDeleting;
      
      // _animateMultiplierと_animateSignChangeはtrueに設定（Undo/Redo時のアニメーション用）
      pouchAny._animateMultiplier = true;
      pouchAny._animateSignChange = true;
      
      return cleanPouch;
    });

    const cleanGroups = groups.map(group => {
      const cleanGroup = { ...group };
      const groupAny = cleanGroup as any;
      
      // 各種UIフラグを削除
      delete groupAny._isCloning;
      delete groupAny._animateSubtotal;
      
      return cleanGroup;
    });

    const snapshot: SheetSnapshot = {
      id: `snapshot_${Date.now()}`,
      timestamp: new Date(),
      name,
      description,
      pouches: cleanPouches,
      groups: cleanGroups
    };

    return {
      ...sheet,
      snapshots: [...(sheet.snapshots || []), snapshot]
    };
  }

  static restoreSnapshot(
    sheet: Sheet,
    snapshotId: string
  ): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    const snapshot = sheet.snapshots?.find(s => s.id === snapshotId);
    if (!snapshot) return null;

    // スナップショットの状態を履歴に追加
    const sheetWithHistory = this.addEntry(
      sheet,
      snapshot.groups,
      snapshot.pouches,
      HistoryActionType.RESTORE_SNAPSHOT,
      getHistoryStorageCopy().snapshot.loadAction
    );

    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      sheet.pouches,
      snapshot.pouches,
      sheet.groups,
      snapshot.groups
    );

    return {
      newSheet: sheetWithHistory,
      newPouches: animationResult.pouches,
      newGroups: animationResult.groups
    };
  }

  static updateSnapshotName(sheet: Sheet, snapshotId: string, newName: string): Sheet {
    return {
      ...sheet,
      snapshots: sheet.snapshots?.map(snapshot =>
        snapshot.id === snapshotId ? { ...snapshot, name: newName } : snapshot
      ) || []
    };
  }

  static deleteSnapshot(sheet: Sheet, snapshotId: string): Sheet {
    return {
      ...sheet,
      snapshots: sheet.snapshots?.filter(s => s.id !== snapshotId) || []
    };
  }

  static overwriteSnapshot(
    sheet: Sheet,
    pouches: Pouch[],
    groups: Group[],
    snapshotId: string
  ): Sheet {
    // 一時的なプロパティを除外
    const cleanPouches = pouches.map(pouch => {
      const cleanPouch = { ...pouch };
      const pouchAny = cleanPouch as any;
      
      delete pouchAny._animateFromZero;
      delete pouchAny._isDragging;
      delete pouchAny._isMoving;
      delete pouchAny._isDeleting;
      
      pouchAny._animateMultiplier = true;
      pouchAny._animateSignChange = true;
      
      return cleanPouch;
    });

    const cleanGroups = groups.map(group => {
      const cleanGroup = { ...group };
      const groupAny = cleanGroup as any;
      
      delete groupAny._isCloning;
      delete groupAny._animateSubtotal;
      
      return cleanGroup;
    });

    return {
      ...sheet,
      snapshots: sheet.snapshots?.map(snapshot =>
        snapshot.id === snapshotId
          ? {
              ...snapshot,
              timestamp: new Date(),
              pouches: cleanPouches,
              groups: cleanGroups
            }
          : snapshot
      ) || []
    };
  }

  /**
   * 現在の履歴エントリを削除してundo（一つ前のエントリに移動）
   * @param sheet 現在のシート
   * @returns 新しいシート状態とアニメーション用のパウチ・グループ配列
   */
  static shiftUndo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex <= 0) return null;

    const currentState = sheet.history[sheet.historyIndex];
    const targetState = sheet.history[sheet.historyIndex - 1];
    
    // 現在の履歴エントリを削除
    const newHistory = sheet.history.filter((_, index) => index !== sheet.historyIndex);
    const newHistoryIndex = sheet.historyIndex - 1;
    
    const newSheet = {
      ...sheet,
      history: newHistory,
      historyIndex: newHistoryIndex,
      balancingPouchId: targetState.balancingPouchId ?? null,
      balancingTargetTotal: targetState.balancingTargetTotal,
      balancingPreferredSign: targetState.balancingPreferredSign,
      activeLayerId: activeLayerIdForUndo(sheet, currentState),
      ...layerFieldsFromEntry(sheet, targetState),
    };

    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );

    return {
      newSheet,
      newPouches: animationResult.pouches,
      newGroups: animationResult.groups
    };
  }

  /**
   * 現在の履歴エントリを削除してredo（一つ先のエントリに移動）
   * @param sheet 現在のシート
   * @returns 新しいシート状態とアニメーション用のパウチ・グループ配列
   */
  static shiftRedo(sheet: Sheet): { newSheet: Sheet; newPouches: Pouch[]; newGroups: Group[] } | null {
    if (!sheet.history || sheet.historyIndex >= sheet.history.length - 1) return null;

    const currentState = sheet.history[sheet.historyIndex];
    const targetState = sheet.history[sheet.historyIndex + 1];
    
    // 現在の履歴エントリを削除
    const newHistory = sheet.history.filter((_, index) => index !== sheet.historyIndex);
    // 削除後、historyIndexはそのまま（配列がずれるため）
    const newHistoryIndex = sheet.historyIndex;
    
    const newSheet = {
      ...sheet,
      history: newHistory,
      historyIndex: newHistoryIndex,
      balancingPouchId: targetState.balancingPouchId ?? null,
      balancingTargetTotal: targetState.balancingTargetTotal,
      balancingPreferredSign: targetState.balancingPreferredSign,
      activeLayerId: activeLayerIdForRedo(sheet, targetState),
      ...layerFieldsFromEntry(sheet, targetState),
    };

    // 統一的なアニメーション判定を適用
    const animationResult = this.applyAnimationFlags(
      currentState.pouches,
      targetState.pouches,
      currentState.groups,
      targetState.groups
    );

    return {
      newSheet,
      newPouches: animationResult.pouches,
      newGroups: animationResult.groups
    };
  }

  /**
   * 現在の状態と復元後の状態を比較して、アニメーション用フラグを自動設定
   * @param currentPouches 現在のパウチ配列
   * @param targetPouches 復元後のパウチ配列
   * @param currentGroups 現在のグループ配列
   * @param targetGroups 復元後のグループ配列
   * @returns アニメーション用フラグが設定されたパウチとグループ配列
   */
  static applyAnimationFlags(
    currentPouches: Pouch[],
    targetPouches: Pouch[],
    currentGroups: Group[],
    targetGroups: Group[]
  ): { pouches: Pouch[]; groups: Group[] } {
    // パウチのアニメーション判定
    const currentPouchIds = new Set(currentPouches.map(p => p.id));
    const targetPouchIds = new Set(targetPouches.map(p => p.id));
    
    // 削除されるパウチID = 現在にあって復元後にないパウチID
    const deletedPouchIds = Array.from(currentPouchIds).filter(id => !targetPouchIds.has(id));
    // 追加されるパウチID = 復元後にあって現在にないパウチID
    const addedPouchIds = Array.from(targetPouchIds).filter(id => !currentPouchIds.has(id));
    
    // パウチにアニメーションフラグを設定
    const animatedPouches = targetPouches.map(pouch => {
      const currentPouch = currentPouches.find(p => p.id === pouch.id);
      
      if (addedPouchIds.includes(pouch.id)) {
        // 追加されるパウチ：_animateFromZeroフラグ
        return { ...pouch, _animateFromZero: true } as Pouch;
      } else if (currentPouch) {
        // 既存のパウチ：値やマルチの変更をチェック
        const hasValueChange = currentPouch.value !== pouch.value;
        const hasMultiplierChange = currentPouch.multiplier !== pouch.multiplier;
        
        return {
          ...pouch,
          _animateSignChange: hasValueChange,
          _animateMultiplier: hasMultiplierChange
        } as Pouch;
      }
      return pouch;
    });
    
    // 削除されるパウチを一時的に追加（_isDeletingフラグ付き）
    const deletedPouches = currentPouches
      .filter(p => deletedPouchIds.includes(p.id))
      .map(p => ({ ...p, _isDeleting: true } as Pouch));
    
    const finalPouches = [...animatedPouches, ...deletedPouches];
    
    // グループのアニメーション判定
    const currentGroupIds = new Set(currentGroups.map(g => g.id));
    const targetGroupIds = new Set(targetGroups.map(g => g.id));
    
    // 削除されるグループID = 現在にあって復元後にないグループID
    const deletedGroupIds = Array.from(currentGroupIds).filter(id => !targetGroupIds.has(id));
    // 追加されるグループID = 復元後にあって現在にないグループID
    const addedGroupIds = Array.from(targetGroupIds).filter(id => !currentGroupIds.has(id));
    
    // グループにアニメーションフラグを設定
    const animatedGroups = targetGroups.map(group => {
      if (addedGroupIds.includes(group.id)) {
        // 追加されるグループ：_isAddingフラグ
        return { ...group, _isAdding: true, _isDeleting: false } as Group;
      }
      return { ...group, _isAdding: false, _isDeleting: false } as Group;
    });
    
    // 削除されるグループを一時的に追加（_isDeletingフラグ付き）
    const deletedGroups = currentGroups
      .filter(g => deletedGroupIds.includes(g.id))
      .map(g => ({ ...g, _isDeleting: true, _isAdding: false } as Group));
    
    const finalGroups = [...animatedGroups, ...deletedGroups];
    
    return {
      pouches: finalPouches,
      groups: finalGroups
    };
  }

  /** Bridge/API write 用: applyAnimationFlags に位置移動・グループ所属変更を追加 */
  static applyBridgeWriteAnimationFlags(
    currentPouches: Pouch[],
    targetPouches: Pouch[],
    currentGroups: Group[],
    targetGroups: Group[]
  ): { pouches: Pouch[]; groups: Group[] } {
    const base = this.applyAnimationFlags(
      currentPouches,
      targetPouches,
      currentGroups,
      targetGroups,
    );

    const pouches = base.pouches.map(pouch => {
      const current = currentPouches.find(p => p.id === pouch.id);
      if (current && (current.x !== pouch.x || current.y !== pouch.y)) {
        return { ...pouch, _isMoving: true } as Pouch;
      }
      return pouch;
    });

    const groups = base.groups.map(group => {
      const current = currentGroups.find(g => g.id === group.id);
      if (!current) return group;
      const oldIds = [...(current.pouches ?? [])].sort().join(',');
      const newIds = [...(group.pouches ?? [])].sort().join(',');
      if (oldIds !== newIds) {
        return { ...group, _animateSubtotal: true } as Group;
      }
      return group;
    });

    return { pouches, groups };
  }
} 